import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import { useState, type ReactElement, type ReactNode } from "react";
import {
  ArrowDown,
  BarChart3,
  ChevronDown,
  ExternalLink,
  MessageSquare,
  NotebookPen,
  Upload,
} from "lucide-react";
import { ApiRequestError, authenticatedApi } from "@/lib/api";
import { Badge } from "@/components/badge";
import { cn } from "@/lib/utils";
import { AdminGuard, AdminNav } from "./shared";

const USAGE_DAY_OPTIONS = [7, 14, 30, 90] as const;

type AdminUsageDays = (typeof USAGE_DAY_OPTIONS)[number];

type AdminUsageSort = "total" | "uploads" | "chat" | "observations" | "last" | "name";

interface AdminUsageRdRow {
  rdId: string;
  rdSlug: string;
  rdName: string;
  totalActions: number;
  uploads: number;
  chatMessages: number;
  observations: number;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
}

interface AdminUsageRow {
  userId: string;
  name: string;
  email: string;
  role: "admin" | "clinical_admin" | "supervisor";
  deletedAt: string | null;
  totalActions: number;
  uploads: number;
  chatMessages: number;
  observations: number;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  rds: AdminUsageRdRow[];
}

interface AdminUsageDashboard {
  days: AdminUsageDays;
  sort: AdminUsageSort;
  startAt: string;
  endAt: string;
  rows: AdminUsageRow[];
  totals: {
    activeUsers: number;
    totalActions: number;
    uploads: number;
    chatMessages: number;
    observations: number;
  };
}

const SORT_LABELS: Record<AdminUsageSort, string> = {
  total: "Total",
  uploads: "Uploads",
  chat: "Chat",
  observations: "Observations",
  last: "Last active",
  name: "Name",
};

const DATE_TIME = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

const DATE_ONLY = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  month: "short",
  day: "numeric",
});

export function AdminUsage(): ReactElement {
  const { getToken } = useAuth();
  const search = useSearch({ strict: false }) as {
    days?: number | string;
    sort?: string;
  };
  const days = normalizeUsageDays(Number(search.days));
  const sort = normalizeUsageSort(search.sort);
  const usage = useQuery({
    queryKey: ["admin-usage", days, sort],
    queryFn: () =>
      authenticatedApi<{ data: AdminUsageDashboard }>(
        getToken,
        `/api/admin/usage?days=${days}&sort=${sort}`
      ),
  });

  if (usage.error) {
    const error = usage.error;

    if (error instanceof ApiRequestError) {
      return (
        <AdminGuard>
          <div className="min-h-screen bg-background flex items-center justify-center">
            <p className="text-lg text-muted-foreground">
              {error.status} — {apiErrorMessage(error)}
            </p>
          </div>
        </AdminGuard>
      );
    }

    return (
      <AdminGuard>
        <div className="max-w-[1200px] mx-auto px-6 py-8">
          <p className="text-sm text-muted-foreground">Unable to load usage.</p>
        </div>
      </AdminGuard>
    );
  }

  if (!usage.data) {
    return (
      <AdminGuard>
        <div className="max-w-[1200px] mx-auto px-6 py-8">
          <p className="text-sm text-muted-foreground">Loading usage...</p>
        </div>
      </AdminGuard>
    );
  }

  return (
    <AdminGuard>
      <UsageClient usage={usage.data.data} />
    </AdminGuard>
  );
}

function UsagePageHead() {
  return (
    <div className="flex items-start justify-between gap-6 mb-6">
      <div>
        <div className="text-caption uppercase tracking-wider text-muted-foreground font-medium">
          Admin
        </div>
        <h1 className="text-2xl font-bold font-sans text-foreground">Usage</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Counts v2 supervisor/admin uploads, chat turns, and observation notes. Expand
          a user to see activity by RD. VA and RD accounts are excluded from this
          supervisor-activity report.
        </p>
      </div>
    </div>
  );
}

function UsageWindowPicker({ usage }: { usage: AdminUsageDashboard }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
      <div className="inline-flex rounded-lg border border-border bg-card p-1 shadow-sm">
        {USAGE_DAY_OPTIONS.map((option) => (
          <Link
            key={option}
            to="/admin/usage"
            search={{ days: option, sort: usage.sort }}
            className={cn(
              "h-8 px-3 rounded-md text-xs font-medium inline-flex items-center",
              usage.days === option
                ? "bg-bark text-white"
                : "text-sand-500 hover:text-foreground hover:bg-sand-50"
            )}
          >
            {option}d
          </Link>
        ))}
      </div>
      <div className="text-xs text-muted-foreground">
        {formatDate(usage.startAt)} to {formatDateTime(usage.endAt)}
      </div>
    </div>
  );
}

function UsageTotals({ totals }: { totals: AdminUsageDashboard["totals"] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
      <MetricTile
        icon={<BarChart3 className="h-4 w-4" />}
        label="Active users"
        value={totals.activeUsers}
      />
      <MetricTile
        icon={<BarChart3 className="h-4 w-4" />}
        label="Total actions"
        value={totals.totalActions}
      />
      <MetricTile
        icon={<Upload className="h-4 w-4" />}
        label="Uploads"
        value={totals.uploads}
      />
      <MetricTile
        icon={<MessageSquare className="h-4 w-4" />}
        label="Chat messages"
        value={totals.chatMessages}
      />
      <MetricTile
        icon={<NotebookPen className="h-4 w-4" />}
        label="Observations"
        value={totals.observations}
      />
    </div>
  );
}

function UsageTableHeader({
  sort,
  days,
}: {
  sort: AdminUsageDashboard["sort"];
  days: AdminUsageDashboard["days"];
}) {
  return (
    <div className="grid grid-cols-[32px_2fr_1fr_1fr_1fr_1fr_1.2fr_1.2fr] gap-4 py-3 px-5 bg-muted border-b border-border text-xs tracking-wider text-muted-foreground font-medium uppercase">
      <div />
      <SortHeader label="User" sort="name" currentSort={sort} days={days} />
      <SortHeader
        label="Total"
        sort="total"
        currentSort={sort}
        days={days}
        align="right"
      />
      <SortHeader
        label="Uploads"
        sort="uploads"
        currentSort={sort}
        days={days}
        align="right"
      />
      <SortHeader
        label="Chat"
        sort="chat"
        currentSort={sort}
        days={days}
        align="right"
      />
      <SortHeader
        label="Observations"
        sort="observations"
        currentSort={sort}
        days={days}
        align="right"
      />
      <div>First seen</div>
      <SortHeader label="Last active" sort="last" currentSort={sort} days={days} />
    </div>
  );
}

function UsageClient({ usage }: { usage: AdminUsageDashboard }) {
  const [expandedUserIds, setExpandedUserIds] = useState<Set<string>>(() => new Set());

  function toggleUser(userId: string) {
    setExpandedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);

      return next;
    });
  }

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-8">
      <AdminNav />
      <UsagePageHead />

      <UsageWindowPicker usage={usage} />

      <UsageTotals totals={usage.totals} />

      <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
        <UsageTableHeader sort={usage.sort} days={usage.days} />

        {usage.rows.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            No supervisor or admin activity in this window.
          </div>
        ) : (
          usage.rows.map((row) => (
            <UsageTableRow
              key={row.userId}
              row={row}
              expanded={expandedUserIds.has(row.userId)}
              onToggle={() => toggleUser(row.userId)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function MetricTile({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: number;
}) {
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm px-5 py-4">
      <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wider">
        {icon}
        {label}
      </div>
      <div className="mt-2 text-3xl font-semibold text-foreground">
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function SortHeader({
  label,
  sort,
  currentSort,
  days,
  align = "left",
}: {
  label: string;
  sort: AdminUsageSort;
  currentSort: AdminUsageSort;
  days: AdminUsageDays;
  align?: "left" | "right";
}) {
  const active = sort === currentSort;

  return (
    <Link
      to="/admin/usage"
      search={{ days, sort }}
      className={cn(
        "inline-flex items-center gap-1 hover:text-foreground",
        align === "right" && "justify-end text-right",
        active && "text-foreground"
      )}
      title={`Sort by ${SORT_LABELS[sort]}`}
    >
      {label}
      {active && <ArrowDown className="h-3 w-3" />}
    </Link>
  );
}

function UsageRdBreakdown({ rds }: { rds: AdminUsageRow["rds"] }) {
  if (rds.length === 0) {
    return (
      <div className="border-b border-sand-100 bg-sand-50/50 px-5 py-4">
        <div className="pl-[48px] text-label text-muted-foreground">
          No RD-level activity in this window.
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-sand-100 bg-sand-50/50 px-5 py-4">
      <div className="ml-[48px] rounded-lg border border-border bg-card overflow-hidden">
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1.1fr_auto] gap-4 px-4 py-2 bg-muted text-caption uppercase tracking-wider text-muted-foreground font-medium">
          <div>RD</div>
          <div className="text-right">Total</div>
          <div className="text-right">Uploads</div>
          <div className="text-right">Chat</div>
          <div className="text-right">Observations</div>
          <div>Last activity</div>
          <div className="text-right">RD page</div>
        </div>
        {rds.map((rd) => (
          <UsageRdRow key={rd.rdId} rd={rd} />
        ))}
      </div>
    </div>
  );
}

function UsageTableRow({
  row,
  expanded,
  onToggle,
}: {
  row: AdminUsageRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className={cn(row.deletedAt && "opacity-55")}>
      <div className="grid w-full grid-cols-[32px_2fr_1fr_1fr_1fr_1fr_1.2fr_1.2fr] gap-4 py-3 px-5 border-b border-sand-100 items-center text-left hover:bg-sand-50/60">
        <button
          type="button"
          onClick={onToggle}
          className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground bg-card hover:text-foreground"
          aria-label={`${expanded ? "Collapse" : "Expand"} ${row.name}'s RD activity`}
          aria-expanded={expanded}
        >
          <ChevronDown
            className={cn("h-3.5 w-3.5 transition-transform", expanded && "rotate-180")}
          />
        </button>
        <span className="min-w-0">
          <span className="flex items-center gap-2 min-w-0">
            <span className="text-body text-foreground font-medium truncate">
              {row.name}
            </span>
            <Badge variant="category" className="shrink-0 text-micro uppercase">
              {row.role.replace("_", " ")}
            </Badge>
          </span>
          <span className="block text-label text-muted-foreground truncate">
            {row.email}
          </span>
        </span>
        <NumberCell value={row.totalActions} />
        <NumberCell value={row.uploads} />
        <NumberCell value={row.chatMessages} />
        <NumberCell value={row.observations} />
        <DateCell value={row.firstSeenAt} />
        <DateCell value={row.lastSeenAt} />
      </div>

      {expanded ? <UsageRdBreakdown rds={row.rds} /> : null}
    </div>
  );
}

function UsageRdRow({ rd }: { rd: AdminUsageRdRow }) {
  return (
    <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1.1fr_auto] gap-4 px-4 py-3 border-t border-sand-100 items-center">
      <div className="min-w-0">
        <div className="text-body text-foreground font-medium truncate">
          {rd.rdName}
        </div>
        <div className="text-caption text-muted-foreground truncate">{rd.rdSlug}</div>
      </div>
      <NumberCell value={rd.totalActions} />
      <NumberCell value={rd.uploads} />
      <NumberCell value={rd.chatMessages} />
      <NumberCell value={rd.observations} />
      <DateCell value={rd.lastSeenAt} />
      <Link
        to="/rds/$rdId"
        params={{ rdId: rd.rdId }}
        className="inline-flex items-center justify-end gap-1 text-label font-medium text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
      >
        Open RD
        <ExternalLink className="h-3 w-3" />
      </Link>
    </div>
  );
}

function NumberCell({ value }: { value: number }) {
  return (
    <div className="text-right text-body font-semibold text-foreground">
      {value.toLocaleString()}
    </div>
  );
}

function DateCell({ value }: { value: string | null }) {
  return (
    <div className="text-label text-muted-strong">
      {value ? formatDateTime(value) : "—"}
    </div>
  );
}

function normalizeUsageDays(days: number | undefined): AdminUsageDays {
  if (USAGE_DAY_OPTIONS.includes(days as AdminUsageDays)) {
    return days as AdminUsageDays;
  }

  return 14;
}

function normalizeUsageSort(sort: string | null | undefined): AdminUsageSort {
  switch (sort) {
    case "uploads":
    case "chat":
    case "observations":
    case "last":
    case "name":
      return sort;
    default:
      return "total";
  }
}

function formatDateTime(value: string) {
  return DATE_TIME.format(new Date(value));
}

function formatDate(value: string) {
  return DATE_ONLY.format(new Date(value));
}

function apiErrorMessage(error: ApiRequestError): string {
  const body = error.body as { error?: string } | undefined;

  return body?.error ?? error.message;
}
